import React, { useCallback, useMemo, useRef, useState } from 'react';
import { GestureResponderEvent, PanResponder, PanResponderGestureState, View, Pressable } from 'react-native';

import GestureHandlerStyles from './styles';

export interface GestureData {
    value?: unknown;
    callback: (...args: any[]) => unknown;
    vertical: { from: number; to: number };
    horizontal: { from: number; to: number };
    direction: GESTURE_DIRECTIONS;
    threshold: number;
}

export interface GesturesData {
    [gestureName: string]: GestureData;
}

// eslint-disable-next-line no-shadow
export enum GESTURE_DIRECTIONS {
    VERTICAL = 'vertical',
    HORIZONTAL = 'horizontal'
}

// eslint-disable-next-line no-shadow
enum GESTURE_NAMES {
    VOLUME = 'volume',
    BRIGHTNESS = 'brightness',
    HORIZONTAL = 'horizontal'
}

const getStartLocation = (moveX: number, dx: number, fullsize: number) =>
    ((moveX - dx) / fullsize) * 100;

interface GestureHandlerProps {
    children: React.ReactElement | React.ReactElement[];
    showUI: () => void;
    onOverlayPress: () => void;
    disable: boolean;
    gestureOptions: GesturesData;
}

const GestureHandler = ({
    children,
    showUI,
    onOverlayPress,
    disable,
    gestureOptions
}: GestureHandlerProps): React.ReactElement => {
    const dimensions = useRef({ height: 0, width: 0 });
    const eventToHandle = useRef<(GestureData & { gestureName: string }) | null>(null);
    const [opacity, setOpacity] = useState(0.5);

    const validateEvent = useCallback(
        (evt: GestureResponderEvent, gestureState: PanResponderGestureState) => {
            if (dimensions.current.height === 0 || dimensions.current.width === 0) {
                return false;
            }
            const { dx, dy, moveX, moveY } = gestureState;

            const gestureStartXLocation = getStartLocation(moveX, dx, dimensions.current.width);
            const gestureStartYLocation = getStartLocation(moveY, dy, dimensions.current.height);

            return Object.entries(gestureOptions).some(([gestureName, gestureData]) => {
                const gesturePointsToHandle =
                    gestureData.direction === GESTURE_DIRECTIONS.HORIZONTAL
                        ? Number(dx)
                        : Number(dy);

                const hasGestureBypassedThreshold =
                    Math.abs(gesturePointsToHandle) - gestureData.threshold > 0;

                const isGestureActivationZone =
                    gestureStartXLocation >= gestureData.horizontal.from &&
                    gestureStartXLocation <= gestureData.horizontal.to &&
                    gestureStartYLocation >= gestureData.vertical.from &&
                    gestureStartYLocation <= gestureData.vertical.to;

                if (hasGestureBypassedThreshold && isGestureActivationZone) {
                    eventToHandle.current = { ...gestureData, gestureName };
                    return true;
                }
                return false;
            });
        },
        [gestureOptions]
    );

    const handleAction = useCallback(
        (actionPoints) => {
            if (!eventToHandle.current) return;

            const { callback: gestureCallback, gestureName } = eventToHandle.current;
            // const brightness = gestureOptions.brightness.value;
            // const volume = gestureOptions.volume.value;

            switch (gestureName) {
                // case GESTURE_NAMES.BRIGHTNESS:
                //     gestureCallback(Math.max(0, Math.min(1, brightness + actionPoints)));
                //     break;

                // case GESTURE_NAMES.VOLUME:
                //     gestureCallback(Math.max(0, Math.min(1, volume + actionPoints)));
                //     break;

                case GESTURE_NAMES.HORIZONTAL:
                    gestureCallback(actionPoints);
                    break;
                default:
                    break;
            }
        },
        [gestureOptions]
    );

    const onMove = useCallback(
        (_evt: GestureResponderEvent, gestureEvent: PanResponderGestureState) => {
            if (!eventToHandle.current) return;

            const { direction } = eventToHandle.current;

            const { dx, dy } = gestureEvent;

            const gesturePointsToHandle = direction === GESTURE_DIRECTIONS.HORIZONTAL ? dx : dy;
            const dimensionToHandle =
                direction === GESTURE_DIRECTIONS.HORIZONTAL
                    ? dimensions.current.width
                    : dimensions.current.height;
            const actionPoints = gesturePointsToHandle / dimensionToHandle;

            setOpacity(Math.max(-1, Math.min(1, opacity + -actionPoints)));
            handleAction(-actionPoints * 1.1);
        },
        [handleAction, opacity, eventToHandle]
    );

    const onReleaseResponder = useCallback(() => {
        eventToHandle.current = null;
        showUI();
    }, [showUI]);

    const panResponder = useMemo(
        () =>
            PanResponder.create({
                // On Touch down
                // onStartShouldSetPanResponder: () => {
                //     onOverlayPress();
                //     return false;
                // },
                // When moving started
                onMoveShouldSetPanResponderCapture: validateEvent,
                // When Responding
                onPanResponderMove: onMove,
                // On Touch Up
                onPanResponderRelease: onReleaseResponder
            }),
        [onMove, onReleaseResponder, showUI, validateEvent]
    );

    return (
        <View
            style={GestureHandlerStyles.container}
            onLayout={(event) => {
                const { width, height } = event.nativeEvent.layout;
                dimensions.current = { width, height };
            }}
            // Unless we have dimensions set, do not use gestures
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(!disable ? panResponder.panHandlers : {})}
        >
            <Pressable
                // style={GestureHandlerStyles.container}
                onPress={() => { onOverlayPress(); }}>
                {children}
            </Pressable>
        </View>
    );
};

export default GestureHandler;
